import React, { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import amu from './Assets/Xianhe New Assets/ourservices/a-mu.png';
import knowyourself from './Assets/Xianhe New Assets/ourservices/knowyourself.png';
import studio from './Assets/Xianhe New Assets/ourservices/studio.png';
import theexperts from './Assets/Xianhe New Assets/ourservices/theexperts.png';
import wheel from './Assets/Xianhe New Assets/ourservices/thewheel.png';
// import thewheel from './Assets/Xianhe New Assets/natal-assets/NewFS.png';
import ring from './Assets/Xianhe New Assets/ourservices/ring.png';

// Static data — API calls are commented out below
const STATIC_TITLE = "Start Your Journey";
const STATIC_SUBTITLE =
  "Your destiny is more than a single reading — it’s an evolving journey. Explore five pillars of guidance, each designed to help you gain clarity, grow, and move forward with confidence.";

const STATIC_SERVICES = [
  {
    key: 'knowYourself',
    title: 'Know Yourself',
    desc: 'A space for insights across health, career, relationships, and more — updated daily, monthly, and yearly.',
    icon: knowyourself,
    route: '/chart',
  },
  {
    key: 'theWheel',
    title: 'The Wheel',
    desc: 'A modern ritual for reflection — offering symbolic insights to help you pause, consider, and move forward with clarity.',
    icon: wheel,
    route: '/fortune-stick',
  },
  {
    key: 'theExperts',
    title: 'The Experts',
    desc: 'Connecting you with experienced practitioners for personalised consultations — helping you explore your questions with greater clarity and confidence.',
    icon: theexperts,
    route: '/schedule',
  },
  {
    key: 'studio',
    title: 'Studio',
    desc: 'A range of courses and online classes designed to support your personal growth, learning, and self-development.',
    icon: studio,
    route: '/courses',
  },
  {
    key: 'amu',
    title: 'A-MU',
    desc: "Featuring thoughtfully designed digital pieces with symbolic elements — created to support clarity, balance, and different aspects of daily life.",
    icon: amu,
    route: '/shop',
  },
];

export default function OurServices() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const MOBILE_BREAKPOINT = 768;
  const COMPACT_DESKTOP_MAX_WIDTH = 1500;
  const COMPACT_DESKTOP_MAX_HEIGHT = 900;

  // Static title & description (kept as fallback defaults)
  // const servicesTitle = STATIC_TITLE;
  // const servicesDescription = STATIC_SUBTITLE;
  // const services = STATIC_SERVICES;
  const [servicesTitle, setServicesTitle] = useState(STATIC_TITLE);
  const [servicesDescription, setServicesDescription] = useState(STATIC_SUBTITLE);
  const [services, setServices] = useState(STATIC_SERVICES);

  const [showAppOnlyPopup, setShowAppOnlyPopup] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);
  const [viewportSize, setViewportSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [isCompactDesktop, setIsCompactDesktop] = useState(
    window.innerWidth >= MOBILE_BREAKPOINT &&
      (window.innerWidth <= COMPACT_DESKTOP_MAX_WIDTH || window.innerHeight <= COMPACT_DESKTOP_MAX_HEIGHT)
  );

  const [langId, setLangId] = useState(localStorage.getItem('languageId') || '2224');

  useEffect(() => {
    const handleStorageChange = () => setLangId(localStorage.getItem('languageId') || '2224');
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Fetch section title & description from API
  useEffect(() => {
    const custId = localStorage.getItem('userID') || '0';
    const langIdValue = langId;
    fetch(`https://xianhefairy.com/LotusFairy_API/api/SiteContent/GetSiteContent?Code=LZ8GO&CustId=${custId}&LangId=${langIdValue}`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data) && data[0]?.Description && data[0]?.Title) {
          setServicesDescription(data[0].Description);
          setServicesTitle(data[0].Title);
        }
      })
      .catch(err => console.error('Failed to fetch services description:', err));
  }, [langId]);

  // Fetch individual service cards from API
  useEffect(() => {
    const langIdValue = langId;
    const serviceCodes = ['IY8BD', 'J6MA3', 'G7RHI', 'DSMHA', 'IOECH'];
    Promise.all(
      serviceCodes.map(code =>
        fetch(`https://xianhefairy.com/LotusFairy_API/api/SiteContent/GetSiteContent?Code=${code}&LangId=${langIdValue}`)
          .then(res => res.json())
      )
    )
      .then(results => {
        const fetched = results.map((data, i) => {
          if (Array.isArray(data) && data[0]?.Title && data[0]?.Description) {
            return { ...STATIC_SERVICES[i], title: data[0].Title, desc: data[0].Description };
          }
          return null;
        }).filter(Boolean);
        if (fetched.length > 0) setServices(fetched);
      })
      .catch(err => console.error('Failed to fetch services data:', err));
  }, [langId]);

  useEffect(() => {
    const handleResize = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      setViewportSize({ width: viewportWidth, height: viewportHeight });
      setIsMobile(viewportWidth < MOBILE_BREAKPOINT);
      setIsCompactDesktop(
        viewportWidth >= MOBILE_BREAKPOINT &&
          (viewportWidth <= COMPACT_DESKTOP_MAX_WIDTH || viewportHeight <= COMPACT_DESKTOP_MAX_HEIGHT)
      );
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [COMPACT_DESKTOP_MAX_HEIGHT, COMPACT_DESKTOP_MAX_WIDTH, MOBILE_BREAKPOINT]);

  const desktopScale = React.useMemo(() => {
    if (isMobile) return 1;
    const widthScale = viewportSize.width / 1440;
    const heightScale = viewportSize.height / 860;
    return Math.max(0.8, Math.min(1, Math.min(widthScale, heightScale)));
  }, [isMobile, viewportSize.height, viewportSize.width]);

  return (
    <section
      id="our-services"
      style={{
        width: '100%',
        padding: isMobile ? '2px 16px' : isCompactDesktop ? '2px 14px' : '2px 20px',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        #our-services .sj-card:hover {
          border-color: rgba(212, 175, 55, 0.6) !important;
          box-shadow: 0 0 20px rgba(212, 175, 55, 0.15) !important;
        }
        @media (max-width: 768px) {
          #our-services .sj-cards-row {
            flex-direction: column !important;
            align-items: center !important;
          }
          #our-services .sj-card {
            max-width: 100% !important;
            width: 100% !important;
          }
        }
      `}</style>

      {/* Heading area with WELCOME watermark behind heading + subtitle */}
      <div style={{ position: 'relative', textAlign: 'center', marginBottom: isMobile ? '42px' : `${Math.max(38, Math.round(52 * desktopScale))}px` }}>
        <svg
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '0%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            overflow: 'visible',
            display: 'block',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
          width={isMobile ? 400 : Math.max(900, Math.round(1400 * desktopScale))}
          height={isMobile ? 80 : Math.max(104, Math.round(150 * desktopScale))}
          opacity={0.3}
        >
          <defs>
            <linearGradient id="welcome-text-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#7B5BA8" />
              <stop offset="100%" stopColor="#3D2468" />
            </linearGradient>
          </defs>
          <text
            x="50%"
            y="50%"
            fill="url(#welcome-text-grad)"
            stroke="none"
            dominantBaseline="central"
            textAnchor="middle"
            style={{
              fontFamily: '"PlayfairDisplay", sans-serif',
              fontWeight: 800,
              fontSize: isMobile ? 80 : Math.max(104, Math.round(150 * desktopScale)),
              letterSpacing: 0,
            }}
          >
            {t('mainPage.welcome')}
          </text>
        </svg>
        {/* Decorative ring overlaid on the watermark */}
        <img
          src={ring}
          alt=""
          style={{
            position: 'absolute',
            bottom: '50%',
            left: isMobile ? '9.5%' : isCompactDesktop ? '19.5%' : '22.5%',
            transform: 'translate(-50%, -50%)',
            width: isMobile ? '120px' : `${Math.max(96, Math.round(126 * desktopScale))}px`,
            height: isMobile ? '120px' : `${Math.max(96, Math.round(126 * desktopScale))}px`,
            objectFit: 'contain',
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 1,
            opacity: 0.95,
          }}
        />
        <h2 style={{
          fontFamily: '"PlayfairDisplay", Georgia, serif',
          fontSize: isMobile ? '1.8rem' : `${Math.max(32, Math.round(40 * desktopScale))}px`,
          lineHeight: isMobile ? '2.4rem' : `${Math.max(34, Math.round(40 * desktopScale))}px`,
          color: '#deaf77',
          fontWeight: 800,
          margin: '0 0 20px',
          position: 'relative',
          zIndex: 1,
          top: isMobile ? '12px' : `${Math.max(16, Math.round(24 * desktopScale))}px`,
        }}>
          {servicesTitle}
        </h2>
        <p style={{
          textAlign: 'center',
          color: '#ffffff',
          fontStyle: 'italic',
          fontFamily: '"DMSans", Georgia, serif',
          maxWidth: '1100px',
          margin: '0 auto',
          marginTop: isMobile ? '52px' : `${Math.max(44, Math.round(60 * desktopScale))}px`,
          fontWeight: 500,
          fontSize: isMobile ? '0.88rem' : `${Math.max(15, Math.round(18 * desktopScale))}px`,
          lineHeight: isMobile ? '1.2rem' : `${Math.max(20, Math.round(24 * desktopScale))}px`,
          position: 'relative',
          zIndex: 1,
        }}>
          {servicesDescription}
        </p>
      </div>

      {/* 5-card grid */}
      <div
        className="sj-cards-row"
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? '16px' : `${Math.max(12, Math.round(20 * desktopScale))}px`,
          justifyContent: 'center',
          maxWidth: '1200px',
          margin: '0 auto',
        }}
      >
        {services.map(service => (
          <div
            key={service.key}
            className="sj-card"
            onClick={() => navigate(service.route)}
            style={{
              flex: '1 1 0',
              minWidth: isMobile ? '100%' : `${Math.max(140, Math.round(160 * desktopScale))}px`,
              maxWidth: isMobile ? '100%' : `${Math.max(180, Math.round(210 * desktopScale))}px`,
              background: '#0a031e',
              border: '1px solid rgba(201, 168, 75, 0.4)',
              borderRadius: '16px',
              padding: isMobile ? '30px 18px 24px' : `${Math.max(26, Math.round(36 * desktopScale))}px ${Math.max(16, Math.round(20 * desktopScale))}px ${Math.max(20, Math.round(28 * desktopScale))}px`,
              textAlign: 'center',
              cursor: 'pointer',
              boxSizing: 'border-box',
              transition: 'border-color 0.25s, box-shadow 0.25s',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <img
              src={service.icon}
              alt={service.title}
              style={{
                width: isMobile ? '74px' : `${Math.max(62, Math.round(80 * desktopScale))}px`,
                height: isMobile ? '74px' : `${Math.max(62, Math.round(80 * desktopScale))}px`,
                objectFit: 'contain',
                marginBottom: isMobile ? '16px' : `${Math.max(12, Math.round(20 * desktopScale))}px`,
              }}
            />
            <h3 style={{
              fontFamily: '"PlayfairDisplay", Georgia, serif',
              color: '#deaf77',
              fontSize: isMobile ? '18px' : `${Math.max(16, Math.round(20 * desktopScale))}px`,
              lineHeight: isMobile ? '20px' : `${Math.max(18, Math.round(20 * desktopScale))}px`,
              fontWeight: 700,
              margin: `0 0 ${Math.max(10, Math.round(14 * desktopScale))}px`,
            }}>
              {service.title}
            </h3>
            <p
              style={{
                color: '#f8f5fc',
                fontSize: isMobile ? '14px' : `${Math.max(12, Math.round(14 * desktopScale))}px`,
                lineHeight: isMobile ? '18px' : `${Math.max(16, Math.round(18 * desktopScale))}px`,
                margin: 0,
              }}
            >
              {service.desc}
            </p>
          </div>
        ))}
      </div>

      {/* App-only popup */}
      {showAppOnlyPopup && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setShowAppOnlyPopup(false)}
        >
          <div
            style={{
              background: '#0d1130',
              border: '2px solid #FFF2D2',
              borderRadius: '18px',
              padding: '32px',
              maxWidth: '400px',
              textAlign: 'center',
              color: '#FFF2D2',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: '2rem', marginBottom: '16px' }}>
              {t('mainPage.onlyAvailableOnApp')}
            </h3>
            <p style={{ marginBottom: '24px', fontSize: '1rem' }}>
              {t('mainPage.downloadAppMessage')}
            </p>
            <button
              onClick={() => setShowAppOnlyPopup(false)}
              style={{
                background: '#D4AF37', color: '#0d1130',
                border: 'none', borderRadius: '8px',
                padding: '12px 32px', fontSize: '1rem',
                cursor: 'pointer', fontWeight: 700,
              }}
            >
              {t('mainPage.close')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
